import { Button, List, ListItem, ListItemText, Stack, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  DialogMessageBox,
} from '@verantos-dev/shared-components';

import { useDeleteAnalyticalVariableDialog } from '../hooks/useDeleteAnalyticalVariableDialog';

export interface DeleteAnalyticalVariableDialogProps {
  analyticalVariableId: string;
  /** Display name shown in the confirmation and success copy. */
  variableName: string;
  onClose: () => void;
  /** Project the delete was triggered from, when known. Omit for the library page. */
  projectId?: string;
}

/**
 * Delete confirmation for an analytical variable (OR-6234). Opened from a row
 * the signed-in user created, on the project artifact list and on the
 * variable library page. Four states, decided by the usage read on open:
 *
 * - **In use** — names what still uses the variable; Delete is unavailable (AC 3).
 * - **Check failed** — the read errored, so what uses the variable is unknown;
 *   Delete is unavailable and the copy offers a retry. Never confirm copy, which
 *   would claim the variable is free to delete on an answer nobody has.
 * - **Edit in progress** — Delete works; the copy says the open edit goes too.
 * - **Plain** — confirm, then delete (AC 2).
 *
 * Mirrors ``DeleteConceptGroupDialog`` on the project page.
 */
export const DeleteAnalyticalVariableDialog = ({
  analyticalVariableId,
  variableName,
  onClose,
  projectId,
}: DeleteAnalyticalVariableDialogProps) => {
  const { formatMessage } = useIntl();
  const {
    onSubmit,
    closeDialog,
    isInUse,
    usageCheckFailed,
    retryUsageCheck,
    usages,
    editInProgress,
    isCheckingUsage,
    isPending,
    submittingError,
    isValidToSubmit,
  } = useDeleteAnalyticalVariableDialog({ analyticalVariableId, onClose, projectId });

  const dialogMessages: ApiWorkflowDialogMessages = {
    loadingCancelButtonText: formatMessage({
      id: 'projects.analyticalVariables.deleteModal.cancelButton',
    }),
    loadingSaveButtonText: formatMessage({
      id: 'projects.analyticalVariables.deleteModal.deleteButton',
    }),
    entryCancelButtonText: formatMessage({
      id: 'projects.analyticalVariables.deleteModal.cancelButton',
    }),
    entrySaveButtonText: formatMessage({
      id: 'projects.analyticalVariables.deleteModal.deleteButton',
    }),
    submittingCancelButtonText: formatMessage({
      id: 'projects.analyticalVariables.deleteModal.cancelButton',
    }),
    submittingSaveButtonText: formatMessage({
      id: 'projects.analyticalVariables.deleteModal.submitting',
    }),
    successCloseButtonText: formatMessage({
      id: 'projects.analyticalVariables.deleteModal.closeButton',
    }),
    submittingErrorTitle: formatMessage({
      id: 'projects.analyticalVariables.deleteModal.submittingError',
    }),
    successTitle: formatMessage({ id: 'projects.analyticalVariables.deleteModal.success.title' }),
    successText: formatMessage(
      { id: 'projects.analyticalVariables.deleteModal.success.text' },
      { variableName }
    ),
  };

  const messageBoxTitleId = usageCheckFailed
    ? 'projects.analyticalVariables.deleteModal.checkFailedTitle'
    : isInUse
    ? 'projects.analyticalVariables.deleteModal.inUseError'
    : 'projects.analyticalVariables.deleteModal.confirmTitle';

  return (
    <ApiWorkflowDialog
      id="deleteAnalyticalVariableDialog"
      title={
        isInUse
          ? formatMessage({ id: 'projects.analyticalVariables.deleteModal.inUseTitle' })
          : formatMessage({ id: 'projects.analyticalVariables.deleteModal.title' })
      }
      onClose={closeDialog}
      onSubmit={onSubmit}
      isSubmitting={isPending}
      dialogMessages={dialogMessages}
      saveButtonColor="destructive"
      submittingError={submittingError}
      isInitialLoading={isCheckingUsage}
      isValidForSubmit={isValidToSubmit}
      hideDisplayButtonsOnSuccess
    >
      <DialogMessageBox
        id="delete-analytical-variable-message-box"
        title={formatMessage({ id: messageBoxTitleId })}
        type={isInUse ? 'warning' : 'error'}
        variant="outlined"
        renderSubmitted={
          usageCheckFailed ? (
            <Stack gap={2} data-testid="delete-analytical-variable-check-failed">
              <Typography>
                {formatMessage({
                  id: 'projects.analyticalVariables.deleteModal.checkFailedDescription',
                })}
              </Typography>
              <Button
                variant="outlined"
                onClick={retryUsageCheck}
                sx={{ alignSelf: 'flex-start' }}
                data-testid="delete-analytical-variable-retry-usage-check"
              >
                {formatMessage({ id: 'projects.analyticalVariables.deleteModal.retryButton' })}
              </Button>
            </Stack>
          ) : isInUse ? (
            <Stack gap={2} data-testid="delete-analytical-variable-in-use">
              <Typography>
                {formatMessage({ id: 'projects.analyticalVariables.deleteModal.inUseDescription' })}
              </Typography>
              <List dense disablePadding>
                {usages.map((usage) => (
                  <ListItem key={`${usage.usageType}-${usage.id}`} disableGutters>
                    <ListItemText
                      primary={usage.name}
                      secondary={formatMessage({
                        id: `projects.analyticalVariables.deleteModal.usage.${usage.usageType}`,
                      })}
                    />
                  </ListItem>
                ))}
              </List>
            </Stack>
          ) : (
            <Stack gap={4} data-testid="delete-analytical-variable-confirm">
              <Typography color="error">
                {formatMessage({ id: 'projects.analyticalVariables.deleteModal.actionUndone' })}
              </Typography>
              <Typography>
                {formatMessage(
                  { id: 'projects.analyticalVariables.deleteModal.description' },
                  { variableName: <b>{variableName}</b> }
                )}
              </Typography>
              {editInProgress && (
                <Typography
                  color="warning.main"
                  data-testid="delete-analytical-variable-edit-in-progress"
                >
                  {formatMessage({ id: 'projects.analyticalVariables.deleteModal.editInProgress' })}
                </Typography>
              )}
            </Stack>
          )
        }
      />
    </ApiWorkflowDialog>
  );
};

export default DeleteAnalyticalVariableDialog;
